import { SQUAD_DEFAULTS } from '../constants/squad';
import { nextWeekdayDates, zonedDateTimeToUtc, type IsoDate } from './dates';
import type { Weekday } from '../enums';

/**
 * Чистые функции планирования сезона (Stage 5, задача 1). Сервис API берёт данные из БД
 * и вызывает их; тесты не требуют базы.
 */

export const SEASON_SLOT_SPORT = 'sport';

/** Код активности для слота шаблона: `sport` → основная активность отряда. */
export function slotActivityCode(slot: string, primaryCode: string): string {
  return slot === SEASON_SLOT_SPORT ? primaryCode : slot;
}

export interface VenueCandidate {
  id: string;
  activityIds: readonly string[];
  capacity: number | null;
  isActive: boolean;
}

export interface VenuePick {
  venueId: string | null;
  /** Площадка меньше отряда: капитан делит состав на два захода (BLUEPRINT 3.5) */
  twoRounds: boolean;
}

/**
 * Автоподбор площадки: первая активная площадка с этой активностью и вместимостью
 * ≥ размера отряда (неизвестная вместимость считается подходящей, но после известной);
 * если все площадки меньше — первая из них с пометкой «два захода».
 *
 * Раунд 5, O2: сбор не основной активности (турнир, баня) сначала ищется среди площадок, где
 * есть и основная активность отряда (`primaryActivityId`), — турнир падел-отряда идёт в
 * падел-клуб, а не в первый зал с турниром. Нет таких — выбор из всех подходящих, как раньше.
 */
export function pickVenue(
  venues: readonly VenueCandidate[],
  activityId: string,
  squadCapacity: number,
  primaryActivityId?: string,
): VenuePick {
  const all = venues.filter((v) => v.isActive && v.activityIds.includes(activityId));
  const home =
    primaryActivityId && primaryActivityId !== activityId
      ? all.filter((v) => v.activityIds.includes(primaryActivityId))
      : [];
  const suitable = home.length > 0 ? home : all;
  if (suitable.length === 0) return { venueId: null, twoRounds: false };
  const fits = suitable.find((v) => v.capacity !== null && v.capacity >= squadCapacity);
  if (fits) return { venueId: fits.id, twoRounds: false };
  const unknown = suitable.find((v) => v.capacity === null);
  if (unknown) return { venueId: unknown.id, twoRounds: false };
  return { venueId: suitable[0]!.id, twoRounds: true };
}

export interface SeasonDateSlot {
  index: number;
  date: IsoDate;
  startsAt: Date;
}

/**
 * Даты сборов: `count` ближайших вечеров отряда в TZ города начиная с `startsOn` (включительно),
 * время начала — `startTime` отряда; момент UTC считается через `zonedDateTimeToUtc` (ADR-11).
 */
export function seasonDates(
  startsOn: IsoDate,
  weekday: Weekday,
  startTime: string,
  timezone: string,
  count: number = SQUAD_DEFAULTS.gatheringsPerSeason,
): SeasonDateSlot[] {
  const anchor = zonedDateTimeToUtc(startsOn, '12:00', timezone);
  return nextWeekdayDates(anchor, weekday, count, timezone).map((date, i) => ({
    index: i + 1,
    date,
    startsAt: zonedDateTimeToUtc(date, startTime, timezone),
  }));
}

/** Пометка «два захода» добавляется к заметкам сбора один раз. */
export function withTwoRoundsNote(notes: string | null, note: string): string {
  if (!notes) return note;
  return notes.includes(note) ? notes : `${notes}\n${note}`;
}

// ───────────────────────────── актуальные сборы ─────────────────────────────

export interface IndexedGathering {
  id: string;
  index: number;
  status: string;
  createdAt: Date;
}

/**
 * Сборы «по расписанию»: у каждого `index` — актуальный сбор (неотменённый, если есть замена;
 * если все отменены — последний созданный). Перенос = новый сбор с тем же `index` (ADR-15).
 */
export function effectiveGatherings<T extends IndexedGathering>(gatherings: readonly T[]): T[] {
  const byIndex = new Map<number, T>();
  for (const g of gatherings) {
    const cur = byIndex.get(g.index);
    if (!cur || (cur.status === 'CANCELLED' && g.status !== 'CANCELLED')) {
      byIndex.set(g.index, g);
    } else if (cur.status === 'CANCELLED' && g.status === 'CANCELLED') {
      if (g.createdAt > cur.createdAt) byIndex.set(g.index, g);
    }
  }
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

/** Сборы по времени начала (раунд персон 1, C2): доп. сбор №8 во вторник идёт перед №3 в четверг. */
export function byStartsAt<T extends { startsAt: string | Date; index: number }>(
  gatherings: readonly T[],
): T[] {
  return [...gatherings].sort(
    (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime() || a.index - b.index,
  );
}

export interface SeasonProgress {
  /** Проведено сборов сезона (без доп. сборов) */
  done: number;
  /** Сборов сезона без доп. сборов и без отменённых — знаменатель «проведено X из 6» */
  total: number;
  /** Доп. сборы (Stage 12 и сборы ops, `isExtra`) без отменённых — «+ N доп.» */
  extra: number;
  /** Первый и последний неотменённый сбор (с доп. сборами) — даты сезона по реальным сборам */
  firstAt: string | null;
  lastAt: string | null;
}

/**
 * Шапка сезона (раунд персон 1, C3): «проведено X из 6 + N доп.» вместо «0 из 8». Сезон — по
 * шаблону, доп. сборы отдельно, отменённые не в знаменателе, даты — первого и последнего сбора,
 * а не `startsOn/endsOn` шаблона. Принимает актуальные сборы (`effectiveGatherings`).
 */
export function seasonProgress(
  gatherings: ReadonlyArray<{ status: string; isExtra: boolean; startsAt: string | Date }>,
): SeasonProgress {
  const live = gatherings.filter((g) => g.status !== 'CANCELLED');
  const main = live.filter((g) => !g.isExtra);
  const times = live.map((g) => new Date(g.startsAt).getTime()).sort((a, b) => a - b);
  const iso = (t: number | undefined) => (t === undefined ? null : new Date(t).toISOString());
  return {
    done: main.filter((g) => g.status === 'DONE').length,
    total: main.length,
    extra: live.length - main.length,
    firstAt: iso(times[0]),
    lastAt: iso(times[times.length - 1]),
  };
}

/** Разовый сбор ops ставится в пределах сезона ± столько дней (ADR-43 п. 5). */
export const EXTRA_GATHERING_MARGIN_DAYS = 7;

/**
 * Окно даты разового сбора ops: от `startsOn − 7` до `endsOn + 7` дней включительно, даты
 * `YYYY-MM-DD` (как `<input type="date">`). Одно правило для сервера (`gathering_out_of_season`) и
 * подсказки формы «Добавить сбор» (раунд персон 1, O5: подсказка показывала сам сезон).
 */
export function extraGatheringWindow(
  startsOn: string | Date,
  endsOn: string | Date,
  /**
   * Раунд персон 2, O7: сегодня в таймзоне города (`YYYY-MM-DD`) — окно не начинается в прошлом.
   * Без него — окно сезона целиком (старое поведение).
   */
  today?: string,
): { from: string; to: string } {
  const margin = EXTRA_GATHERING_MARGIN_DAYS * 86_400_000;
  const day = (d: string | Date) => new Date(new Date(d).toISOString().slice(0, 10)).getTime();
  const iso = (t: number) => new Date(t).toISOString().slice(0, 10);
  const from = iso(day(startsOn) - margin);
  return { from: today && today > from ? today : from, to: iso(day(endsOn) + margin) };
}

/**
 * Почему дату доп. сбора не принять (раунд персон 2, O7) — одно правило для сервера
 * (`admin.seasons.addGathering`) и поля даты в админке: раньше сегодняшнего дня — `gathering_in_past`,
 * вне окна сезона ± неделя — `gathering_out_of_season`, иначе `null`. Время сегодняшнего сбора
 * проверяется отдельно (`gathering_in_past` по минутам).
 */
export function extraGatheringDateBlock(
  date: string,
  window: { from: string; to: string },
  today: string,
): 'gathering_in_past' | 'gathering_out_of_season' | null {
  if (date < today) return 'gathering_in_past';
  return inExtraGatheringWindow(date, window) ? null : 'gathering_out_of_season';
}

/** Дата `YYYY-MM-DD` в окне `extraGatheringWindow` (границы включительно). */
export function inExtraGatheringWindow(
  date: string,
  window: { from: string; to: string },
): boolean {
  return date >= window.from && date <= window.to;
}

/** Сбор перенесён: среди сборов сезона есть более ранний отменённый с тем же `index`. */
export function isRescheduled(g: IndexedGathering, siblings: readonly IndexedGathering[]): boolean {
  return siblings.some(
    (o) => o.id !== g.id && o.index === g.index && o.status === 'CANCELLED' && o.createdAt < g.createdAt,
  );
}
